import React, { useState, useEffect, useMemo } from "react"
import {
  Row,
  Col,
  Card,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap"
import Loader from "pages/Separate/Loader"
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table"
import { versionControlColumn } from "../../../utils/columns/Version Control/versionControlColumn"
import AccessDeniedPopup from "routes/AccessDeniedPopup"
import NewPagination from "components/Common/Newpagination"
import img from "../../../assets/images/version.jpg"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import {
  fetchNodeData,
  FetchNodePostData,
  FetchNodePutData,
} from "../../../utils/common-fetchData"
import Swal from "sweetalert2"
import secureLocalStorage from "react-secure-storage"

function TableContainer({ columns, data, onAddNew, loading }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    pageCount,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [{ id: "id", desc: true }],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )
  const { pageIndex, pageSize, globalFilter } = state

  useEffect(() => {
    setPageSize(10)
  }, [])

  return (
    <Row>
      <Col xl="12">
        <div className="card-body">
          <Row className="align-items-center justify-content-between mb-3">
            <Col className="text-purple fs-5 fw-bold" md="auto">
              Earth Version History
            </Col>
            <Col md="auto">
              <button className="btn btn-save btn-label" onClick={onAddNew}>
                <i className="fas fa-plus me-2 label-icon"></i>Add New
              </button>
            </Col>
          </Row>
          <Row className="mb-3">
          <div className="col-1 me-md-5">
            <select
              className="border-radius form-select"
              style={{ width: "100px" }}
              value={pageSize}
              onChange={e => setPageSize(e.target.value)}
            >
              {[10, 25, 50, 100].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
          </Row>

          <div>
            <Table className="table table-bordered " {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, index) => (
                      <th
                        key={index}
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        className={column.className}
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row)
                  return (
                    <tr key={i} {...row.getRowProps()}>
                      {row.cells.map((cell, index) => (
                        <td
                          key={index}
                          {...cell.getCellProps()}
                          className={cell.column.className}
                          style={{
                            ...cell.column.style,
                            padding: "10px",
                            verticalAlign: "middle",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          <Row className="justify-content-md-end justify-content-center align-items-center">
            <NewPagination
              gotoPage={gotoPage}
              canPreviousPage={canPreviousPage}
              previousPage={previousPage}
              canNextPage={canNextPage}
              pageIndex={pageIndex}
              pageOptions={pageOptions}
              pageSize={pageSize}
              page={page}
              data={data}
              nextPage={nextPage}
            />
          </Row>
        </div>
      </Col>
    </Row>
  )
}

const VersionControl = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [modalViewMode, setModalViewMode] = useState(false)
  const [modalAddMode, setModalAddMode] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentVersion, setCurrentVersion] = useState("0.0.0")
  const [baseVersion, setBaseVersion] = useState("0.0.0")
  const token = secureLocalStorage.getItem("auth_token")
  const [date, setDate] = useState("")
  const handleDateChange = e => {
    const { value } = e.target
    setDate(value)
  }
  const [formState, setFormState] = useState({
    version: "",
    versionType: "",
    description: "",
  })
  const [errors, setErrors] = useState({ version: "", description: "" })
  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchNodeData(setLoading, "versionControl/")
      const success = response?.data?.success || response?.success
      if (success) {
        const versionData =
          response?.data?.versionData || response?.versionData || []
        const mappedVersionData = versionData.map(item => ({
          ...item,
          versionType: item.updateType,
        }))
        setData(mappedVersionData)
        const latestVersion = mappedVersionData.reduce((max, item) => {
          const [maxMajor, maxMinor, maxPatch] = max.version
            .split(".")
            .map(Number)
          const [itemMajor, itemMinor, itemPatch] = item.version
            .split(".")
            .map(Number)
          return itemMajor > maxMajor ||
            (itemMajor === maxMajor && itemMinor > maxMinor) ||
            (itemMajor === maxMajor &&
              itemMinor === maxMinor &&
              itemPatch > maxPatch)
            ? item
            : max
        }, mappedVersionData[0] || { version: "0.0.0" }).version
        setCurrentVersion(latestVersion)
        setBaseVersion(latestVersion)
      } else {
        Swal.fire({
          icon: "warning",
          text: "Failed to fetch data",
        })
      }
    }
    fetchData()
  }, [])

  const validateForm = () => {
    let isValid = true
    const newErrors = { version: "", description: "" }

    if (!formState.version && modalAddMode) {
      newErrors.version = "Please select a version type"
      isValid = false
    } else if (!/^\d+\.\d+\.\d+$/.test(formState.version)) {
      newErrors.version = "Version must be in format X.Y.Z"
      isValid = false
    }

    if (!formState.description.trim()) {
      newErrors.description = "Description is required"
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const onView = record => {
    setSelectedRecord(record)
    setFormState({
      ...record,
      versionType: record.updateType || record.versionType,
    })
    setDate(record.date || "")
    setModalViewMode(true)
    setModalAddMode(false)
    setModalOpen(true)
    setErrors({ version: "", description: "" })
  }

  const onEdit = record => {
    setSelectedRecord(record)
    setFormState({
      ...record,
      versionType: record.updateType || record.versionType,
    })
    setDate(record.date || "")
    setModalViewMode(false)
    setModalAddMode(false)
    setModalOpen(true)
    setErrors({ version: "", description: "" })
  }

  const handleAddNew = () => {
    setFormState({
      version: baseVersion,
      versionType: "",
      description: "",
    })
    setDate("")
    setModalAddMode(true)
    setModalViewMode(false)
    setSelectedRecord(null)
    setModalOpen(true)
    setErrors({ version: "", description: "" })
  }

  const handleVersionTypeChange = type => {
    let [major, minor, patch] = baseVersion.split(".").map(Number)

    switch (type) {
      case "new module":
        major += 1
        minor = 0
        patch = 0
        break
      case "updation":
        minor += 1
        patch = 0
        break
      case "bug fix":
        patch += 1
        break
      default:
        break
    }

    const newVersion = `${major}.${minor}.${patch}`
    setFormState({ ...formState, version: newVersion, versionType: type })
    setErrors({ ...errors, version: "" })
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setModalOpen(false)
    setModalAddMode(false)

    let formData
    if (modalAddMode) {
      formData = {
        version: formState.version,
        updateType: formState.versionType,
        description: formState.description,
        date: date,
      }
    } else {
      formData = {
        id: selectedRecord.id,
        version: formState.version,
        date: date,
        description: formState.description,
      }
    }
    try {
      if (modalAddMode) {
        const response = await FetchNodePostData(
          setLoading,
          "versionControl",
          token,
          formData
        )
        const success = response?.data?.success || response?.success
        console.log(formData)
        if (success) {
          const fetchResponse = await fetchNodeData(
            setLoading,
            "versionControl/"
          )
          const fetchSuccess =
            fetchResponse?.data?.success || fetchResponse?.success
          if (fetchSuccess) {
            const versionData =
              fetchResponse?.data?.versionData ||
              fetchResponse?.versionData ||
              []
            const mappedVersionData = versionData.map(item => ({
              ...item,
              versionType: item.updateType,
            }))
            setData(mappedVersionData)
            const latestVersion = mappedVersionData.reduce((max, item) => {
              const [maxMajor, maxMinor, maxPatch] = max.version
                .split(".")
                .map(Number)
              const [itemMajor, itemMinor, itemPatch] = item.version
                .split(".")
                .map(Number)
              return itemMajor > maxMajor ||
                (itemMajor === maxMajor && itemMinor > maxMinor) ||
                (itemMajor === maxMajor &&
                  itemMinor === maxMinor &&
                  itemPatch > maxPatch)
                ? item
                : max
            }, mappedVersionData[0] || { version: "0.0.0" }).version
            setCurrentVersion(latestVersion)
            setBaseVersion(latestVersion)
          }
          Swal.fire({
            icon: "success",
            text: "New version added successfully",
          })
        } else {
          Swal.fire({
            icon: "warning",
            text: "Failed to add new version",
          })
        }
      } else {
        const response = await FetchNodePutData(
          setLoading,
          "versionControl",
          selectedRecord.id,
          formData
        )

        const success = response?.data?.success || response?.success
        if (success) {
          const fetchResponse = await fetchNodeData(
            setLoading,
            "versionControl"
          )
          const fetchSuccess =
            fetchResponse?.data?.success || fetchResponse?.success
          if (fetchSuccess) {
            const versionData =
              fetchResponse?.data?.versionData ||
              fetchResponse?.versionData ||
              []
            const mappedVersionData = versionData.map(item => ({
              ...item,
              versionType: item.updateType,
            }))
            setData(mappedVersionData)
            const latestVersion = mappedVersionData.reduce((max, item) => {
              const [maxMajor, maxMinor, maxPatch] = max.version
                .split(".")
                .map(Number)
              const [itemMajor, itemMinor, itemPatch] = item.version
                .split(".")
                .map(Number)
              return itemMajor > maxMajor ||
                (itemMajor === maxMajor && itemMinor > maxMinor) ||
                (itemMajor === maxMajor &&
                  itemMinor === maxMinor &&
                  itemPatch > maxPatch)
                ? item
                : max
            }, mappedVersionData[0] || { version: "0.0.0" }).version
            setCurrentVersion(latestVersion)
            setBaseVersion(latestVersion)
          }
          Swal.fire({
            icon: "success",
            text: "Version updated successfully",
          })
        } else {
          Swal.fire({
            icon: "warning",
            text: "Failed to update version",
          })
        }
      }
    } catch (error) {
      console.error("Error in handleSave:", error)
      Swal.fire({
        icon: "warning",
        text: "An error occurred while saving the version",
      })
    }
  }

  const columns = useMemo(
    () =>
      versionControlColumn({
        onView,
        onEdit,
      }),
    []
  )

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div>
        <Row>
          <Col lg="12">
            <Card>
              <TableContainer
                columns={columns}
                data={data}
                onAddNew={handleAddNew}
                loading={loading}
              />
            </Card>
          </Col>
        </Row>

        <Modal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          size="xl"
          centered
        >
          <ModalHeader toggle={() => setModalOpen(false)}>
            {modalViewMode
              ? "View Version"
              : modalAddMode
              ? "Add Version"
              : "Edit Version"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col
                md="4"
                className="d-flex justify-content-center align-items-center"
              >
                <img
                  src={img}
                  alt="Version Illustration"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </Col>
              <Col md="8">
                <Row className="mb-3">
                  <Col md="12">
                    <div className="d-flex flex-wrap align-items-center gap-4">
                      <div>
                        <Label className="fw-bold d-block">Version</Label>
                        <Input
                          name="version"
                          value={formState.version}
                          disabled={true}
                          style={{ maxWidth: "150px" }}
                          invalid={!!errors.version}
                        />
                        {errors.version && (
                          <div className="text-danger mt-1">
                            {errors.version}
                          </div>
                        )}
                      </div>

                      <div>
                        <Label className="fw-bold d-block hidden" hidden>
                          version Type
                        </Label>
                        <div className="d-flex gap-3 flex-wrap mt-4">
                          <Label className="form-check mb-0 ">
                            <Input
                              type="radio"
                              name="versionType"
                              value="new module"
                              checked={formState.versionType === "new module"}
                              onChange={() =>
                                handleVersionTypeChange("new module")
                              }
                              disabled={modalViewMode || !modalAddMode}
                            />
                            New Module
                          </Label>
                          <Label className="form-check mb-0">
                            <Input
                              type="radio"
                              name="versionType"
                              value="updation"
                              checked={formState.versionType === "updation"}
                              onChange={() =>
                                handleVersionTypeChange("updation")
                              }
                              disabled={modalViewMode || !modalAddMode}
                            />
                            Updation
                          </Label>
                          <Label className="form-check mb-0">
                            <Input
                              type="radio"
                              name="versionType"
                              value="bug fix"
                              checked={formState.versionType === "bug fix"}
                              onChange={() =>
                                handleVersionTypeChange("bug fix")
                              }
                              disabled={modalViewMode || !modalAddMode}
                            />
                            Bug Fix
                          </Label>
                        </div>
                      </div>

                      <div>
                        <Label className="fw-bold d-block">Date</Label>
                        <Input
                          type="date"
                          id="date"
                          name="date"
                          className="form-control"
                          value={date}
                          onChange={handleDateChange}
                          disabled={modalViewMode}
                          style={{ maxWidth: "180px" }}
                        />
                      </div>
                    </div>
                  </Col>

                  <Col md="12" className="mb-3 mt-3">
                    <Label className="fw-bold">Description</Label>
                    <div
                      className={`ck-editor-container ${
                        modalViewMode ? "ck-disabled" : ""
                      }`}
                    >
                      <CKEditor
                        editor={ClassicEditor}
                        data={formState.description}
                        disabled={modalViewMode}
                        onReady={editor => {
                          if (modalViewMode) editor.isReadOnly = true
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData()
                          setFormState({ ...formState, description: data })
                          setErrors({ ...errors, description: "" })
                        }}
                      />
                      {errors.description && (
                        <div className="text-danger mt-1">
                          {errors.description}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </ModalBody>

          {!modalViewMode && (
            <ModalFooter className="w-100 border-0 d-flex justify-content-end">
              <div className="text-end">
                <button className="btn btn-save btn-label" onClick={handleSave}>
                  <i className="fas fa-save me-2 label-icon"></i>Save
                </button>
              </div>
            </ModalFooter>
          )}
        </Modal>
        <AccessDeniedPopup modal_Access={false} setAccessDenied={() => {}} />
      </div>
    </React.Fragment>
  )
}

export default VersionControl
